import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import EmptyState from "../../../components/EmptyState";
import {Skeleton} from "antd-mobile";
import useHistoryControl from "../../../hooks/useHistoryControl";
import PickUpLayout, {usePickUpLayout} from "../layouts/PickUpLayout";
import PickUpSearchBar from "./components/PickUpSearchBar";
import PickingUpCard from "./components/PickingUpCard";
import {usePickedUpFailedRetry} from "./hooks/usePickedUpFailedRetry";
import PickUpUploadNotification from "./components/PickUpUploadNotification";
import {t} from "../../../i18n";
import { usePickupTaskAppointmentList } from '../store/pickupStore';
import {PickupAppointmentType} from "../graphql/types/PickupTaskWithAppointmentsType";

const Body: React.FC = () => {
    const [searchValue, setSearchValue] = useState<string>('');
    const [uploadStatus, setUploadStatus] = useState<'retrying' | 'success' | 'none'>('none');
    const [pickingUpDataList, setPickingUpDataList] = useState<PickupAppointmentType[]>([]);
    const {failedUploadCount, isRetrying, showRetryingHint, handleRetry} = usePickedUpFailedRetry();
    const {refreshFailedCount} = usePickUpLayout();
    const { pickupTaskAppointmentList: pickingUpData, loading, refreshPickupTaskAppointmentList } = usePickupTaskAppointmentList();
    const mainContainerRef = useRef<HTMLDivElement>(null);

    useHistoryControl("/pickup/picking-up", {
        useReplace: true,
        controlNavButton: false
    });

    useEffect(() => {
        // 只有在重试数据初始化完成后才执行校验逻辑
        const timer = setTimeout(() => {
            if (failedUploadCount <= 0 && !isRetrying){
                refreshPickupTaskAppointmentList();
            }
        }, 100);

        // 清理定时器，防止内存泄漏
        return () => clearTimeout(timer);
    }, [failedUploadCount, isRetrying, refreshPickupTaskAppointmentList]);

    useEffect(() => {
        if (pickingUpData.length > 0){
            const filteredData = pickingUpData.filter(appointment => !appointment.isFinished);
            setPickingUpDataList(filteredData); // 使用修改后的数据
        }
    }, [pickingUpData]);

    useEffect(() => {
        if (showRetryingHint) {
            setUploadStatus('retrying');
        } else if (uploadStatus === 'retrying') {
            setUploadStatus('success');
            refreshFailedCount();
        }
    }, [showRetryingHint, uploadStatus, refreshFailedCount]);

    const triggerRetry = useCallback(() => {
        if (failedUploadCount > 0 && !isRetrying) {
            handleRetry();
        }
    }, [failedUploadCount, isRetrying, handleRetry]);

    const handleNotificationClick = useCallback(() => {
        if (uploadStatus === 'success') {
            setUploadStatus('none');
        } else if (uploadStatus === 'none' && failedUploadCount > 0) {
            triggerRetry();
        }
    }, [uploadStatus, failedUploadCount, triggerRetry]);

    const handleSearch = async (value: string) => {
        setSearchValue(value);
    };

    const filteredPickingUp = pickingUpDataList.filter(appointment =>
        appointment?.channelName?.toLowerCase().includes(searchValue.toLowerCase()) ?? false
    );

    // 恢复主容器滚动位置
    useLayoutEffect(() => {
        const el = document.getElementById('pickup-main-container');
        const y = Number(sessionStorage.getItem('pickupPageScrollTop') || '0');
        if (el && y > 0) {
            el.scrollTo({ top: y, behavior: 'auto' });
            sessionStorage.removeItem('pickupPageScrollTop'); // 恢复一次后清空
        }
    }, []);

    return (
        <div className="flex flex-col h-screen bg-gray-50 gap-2 p-2" ref={mainContainerRef}>
            <PickUpUploadNotification
                status={uploadStatus}
                onClick={handleNotificationClick}
            />

            <PickUpSearchBar onSearch={handleSearch}/>

            <div className="flex-1">
                {loading ? (
                    <div className="flex flex-col h-screen">
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                    </div>
                ) : (
                    <>
                        {filteredPickingUp.length > 0 ? (
                            <div>
                                {filteredPickingUp.map((pickingUp, index) => (
                                    <PickingUpCard
                                        key={index}
                                        data={pickingUp}
                                    />
                                ))}
                            </div>
                        ) : (
                            <EmptyState description={t(`pages.pickUp.noPickUpInfo`)}/>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};


const pickingUpPage: React.FC = () => {
    return (
        <PickUpLayout title={`pickingUp`}>
            <Body/>
        </PickUpLayout>
    );
};

export default pickingUpPage;